import axios, { AxiosError, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import jsonBigInt from 'json-bigint';
import store from 'features';
import {
  openSnackbar,
  setActiveItem,
  setAjaxLoader
} from 'features/app/appSlice';
import { isValidJson } from 'utils';
import { Config } from 'utils/envConfig';
import { AppModule, EAIEntityTypeCode, PermissionError } from 'utils/constants';
import {
  setEaiContextActions,
  setEaiEtag,
  setPermissionError
} from 'features/eai/eaiSlice';
import { createContextActionPayload } from 'utils/contextActionsUtils';
import {
  getErrorMessage,
  getReconnectionDelay,
  handleAjaxEtag,
  handleAjaxLoader,
  MAX_RETRIES,
  STATUS_CODE
} from 'utils/restUtils';
import {
  setDispatcherContextActions,
  setDispatcherEtag
} from 'features/dispatcher/dispatcherSlice';
import { ContextAction } from 'models/contextActions';
import { EtagEntityList, EtagInterface } from 'models/etag';

declare module 'axios' {
  export interface AxiosRequestConfig {
    hideError404?: boolean;
  }
}

const { dispatch } = store;

export const AJAX_OPERATION_IN_PROGRESS = true;
export const NO_AJAX_OPERATION_IN_PROGRESS = false;

// ==============================|| REST SERVICE ||============================== //

export const apiClient = axios.create({
  withCredentials: true,
  baseURL: Config.apiUrl,
  responseType: 'json'
});

axiosRetry(apiClient, {
  retries: MAX_RETRIES,
  retryDelay: getReconnectionDelay
});

export const authClient = axios.create({
  withCredentials: true,
  baseURL: Config.authUrl,
  responseType: 'json'
});

const onFulfilled = (response: AxiosResponse) => {
  const requestData = response.config?.data;
  const appModule: string | undefined = Object.values(AppModule).find(
    (module: string) => (response.config.url ?? '').indexOf(`${module}/`) > -1
  );
  const etagEntity: EtagEntityList | null | undefined = isValidJson(requestData)
    ? JSON.parse(requestData || null)?.etagEntity
    : null;

  if (response.headers['content-length'] !== 0 && isValidJson(response.data)) {
    response.data = jsonBigInt.parse(response.data);

    const contextActions: ContextAction[] =
      response.data.meta?.contextActions || [];
    if (contextActions.length) {
      // todo: pridat ostatne moduly
      switch (appModule) {
        case AppModule.EAI:
          dispatch(
            setEaiContextActions(
              createContextActionPayload(
                contextActions,
                EAIEntityTypeCode.USER,
                response.config.headers['User-Id']
              )
            )
          );
          break;
        case AppModule.DISPATCHER:
          dispatch(
            setDispatcherContextActions(
              createContextActionPayload(
                contextActions,
                EAIEntityTypeCode.USER,
                response.config.headers['User-Id']
              )
            )
          );
          break;
      }
    }
  }

  handleAjaxLoader(requestData, () =>
    dispatch(setAjaxLoader(NO_AJAX_OPERATION_IN_PROGRESS))
  );

  etagEntity &&
    handleAjaxEtag(requestData, etagEntity, () => {
      const etagPayload: EtagInterface = {
        entity: etagEntity,
        etag: response.headers['etag']
      };

      // todo: pridat ostatne moduly
      switch (appModule) {
        case AppModule.EAI:
          dispatch(setEaiEtag(etagPayload));
          break;
        case AppModule.DISPATCHER:
          dispatch(setDispatcherEtag(etagPayload));
          break;
      }
    });

  return response;
};

const onRejected = (error: AxiosError) => {
  const { config, response } = error;

  const ignoreNotFoundError =
    response?.status === STATUS_CODE.ERROR_NOT_FOUND && config?.hideError404;
  const isPreconditionFail =
    response?.status === STATUS_CODE.ERROR_PRECONDITION_FAILED &&
    !!config?.headers['If-Match'];

  handleAjaxLoader(config?.data, () =>
    dispatch(setAjaxLoader(NO_AJAX_OPERATION_IN_PROGRESS))
  );

  if (ignoreNotFoundError) return;

  if (!isPreconditionFail) {
    const errorMessage = getErrorMessage(response as AxiosResponse<unknown>);

    if (response?.status === STATUS_CODE.ERROR_UNAUTHORIZED) {
      dispatch(setActiveItem([]));
      dispatch(setPermissionError(PermissionError.UNAUTHORIZED_ERROR));
    } else if (errorMessage !== '') {
      dispatch(
        openSnackbar({
          open: true,
          message: errorMessage,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
    }
  }

  return Promise.reject(error);
};

apiClient.interceptors.request.use((request) => {
  request.transformResponse = [(data) => data];

  if (request.data?.showAjaxLoader !== false) {
    dispatch(setAjaxLoader(AJAX_OPERATION_IN_PROGRESS));
  }

  return request;
});

apiClient.interceptors.response.use(onFulfilled, onRejected);
